import React, { useCallback } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage, useIntl } from '@divvy-web/i18n'
import CancelConfirm from '@divvy-web/skylab.cancelconfirm'
import { FormRadioGroup, FormTextInput } from '@divvy-web/skylab.form'
import Icon from '@divvy-web/skylab.icon'
import { FormProvider } from '@divvy-web/skylab.react-hook-form'
import { useForm } from '@divvy-web/skylab.react-hook-form'
import Tooltip from '@divvy-web/skylab.tooltip'

import { NewCollaborator as Collaborator } from '@sputnik/hooks/useApplicationCollaborators/types'

import { useInviteCollaborator } from './InviteCollaborator.gql'
import styles from './styles.module.scss'
import { useUpdateCollaborator } from './UpdateCollaborator.gql'
import { CollaboratorSchemaType, useInviteCollaboratorSchema } from './useNewInviteCollaboratorsSchema'

export interface CollaboratorFormProps {
  collaborator?: Collaborator
  allCollaborators: Collaborator[]
  setActiveCollaboratorId: (id: string | null) => void
  onSubmit: ReturnType<typeof useInviteCollaborator>[0] | ReturnType<typeof useUpdateCollaborator>[0]
  appId: string
}

const CollaboratorForm = ({
  appId,
  collaborator,
  allCollaborators,
  onSubmit,
  setActiveCollaboratorId,
}: CollaboratorFormProps) => {
  const { formatMessage } = useIntl()
  const [getClassName] = useNameFormatter('CollaboratorForm')
  const isEditing = !!collaborator?.id
  const isEditingAdmin = isEditing && !!collaborator?.createAsAdmin
  const adminCount = allCollaborators.filter(({ createAsAdmin }) => createAsAdmin).length
  const atAdminCap = isEditingAdmin ? adminCount > 3 : adminCount >= 3

  const collaboratorSchema = useInviteCollaboratorSchema({ collaborators: allCollaborators })

  const form = useForm<CollaboratorSchemaType>({
    mode: 'all',
    defaultValues: {
      id: collaborator?.id ?? null,
      firstName: collaborator?.firstName ?? '',
      lastName: collaborator?.lastName ?? '',
      email: collaborator?.email ?? '',
      collabRole: collaborator?.createAsAdmin ? 'admin' : 'collaborator',
    },
    resolver: yupResolver(collaboratorSchema),
  })

  const { handleSubmit, control, reset } = form

  const submitPersonForm = useCallback(
    async (data: CollaboratorSchemaType) => {
      await onSubmit({
        variables: {
          salesforceCreditId: appId,
          collaborator: {
            ...(isEditing && { id: data.id }),
            email: data.email,
            firstName: data.firstName,
            lastName: data.lastName,
            createAsAdmin: data.collabRole === 'admin',
          },
        },
      })
    },
    [appId, isEditing, onSubmit],
  )

  return (
    <div>
      <FormProvider {...form}>
        <form className={`${styles[getClassName('')]} dd-privacy-allow`}>
          <div className={styles[getClassName('person-heading')]}>
            <div className={styles[getClassName('person-heading-title')]}>
              {isEditing ? (
                <FormattedMessage
                  defaultMessage='Edit collaborator'
                  id='sputnik.CollaboratorForm__l7n9sP'
                />
              ) : (
                <FormattedMessage
                  defaultMessage='New collaborator'
                  id='sputnik.CollaboratorForm__PPSsAC'
                />
              )}
            </div>
          </div>
          <div className={styles[getClassName('person-names')]}>
            <FormTextInput
              className='dd-privacy-mask'
              control={control}
              label={formatMessage({
                defaultMessage: 'First name',
                id: 'sputnik.CollaboratorForm__pONqz8',
              })}
              name='firstName'
            />
            <FormTextInput
              className='dd-privacy-mask'
              control={control}
              label={formatMessage({
                defaultMessage: 'Last name',
                id: 'sputnik.CollaboratorForm__txUL0F',
              })}
              name='lastName'
            />
          </div>
          <FormTextInput
            className='dd-privacy-mask'
            control={control}
            label={formatMessage({
              defaultMessage: 'Email',
              id: 'sputnik.CollaboratorForm__sy+pv5',
            })}
            name='email'
          />
          <FormRadioGroup
            control={control}
            label={
              <FormattedMessage
                defaultMessage='Role'
                id='sputnik.CollaboratorForm__1ZgrhW'
              />
            }
            name='collabRole'
            options={[
              {
                id: 'collab-role-collaborator',
                label: (
                  <div className={styles[getClassName('role-label')]}>
                    <FormattedMessage
                      defaultMessage='Collaborator'
                      id='sputnik.CollaboratorForm__P1AKC5'
                    />
                    <div className={styles[getClassName('role-input')]}>
                      <Tooltip
                        html={
                          <FormattedMessage
                            defaultMessage='Can fill out the application, make corrections, and upload documents.'
                            id='sputnik.CollaboratorForm__tVZRCX'
                          />
                        }
                      >
                        <Icon name='info' />
                      </Tooltip>
                    </div>
                  </div>
                ),
                value: 'collaborator',
              },
              {
                id: 'collab-role-admin',
                label: (
                  <div className={styles[getClassName('role-label')]}>
                    <FormattedMessage
                      defaultMessage='Admin'
                      id='sputnik.CollaboratorForm__iHN12u'
                    />
                    <div className={styles[getClassName('role-input')]}>
                      <Tooltip
                        html={
                          <FormattedMessage
                            defaultMessage='Can fill out the application and, if approved, manage users, budgets, and spending.'
                            id='sputnik.CollaboratorForm__d87RvP'
                          />
                        }
                      >
                        <Icon name='info' />
                      </Tooltip>
                    </div>
                  </div>
                ),
                value: 'admin',
                disabled: atAdminCap,
                caption: atAdminCap ? (
                  <FormattedMessage
                    defaultMessage='Only 3 admin collaborators are allowed'
                    id='sputnik.CollaboratorForm__rUZpxM'
                  />
                ) : undefined,
              },
            ]}
          />
        </form>
        <CancelConfirm
          confirmText={
            isEditing
              ? formatMessage({
                  defaultMessage: 'Update',
                  id: 'sputnik.CollaboratorForm__BWpuKl',
                })
              : formatMessage({
                  defaultMessage: 'Invite',
                  id: 'sputnik.CollaboratorForm__hYOE+U',
                })
          }
          onCancel={() => {
            setActiveCollaboratorId(null)
            reset()
          }}
          onConfirm={handleSubmit(submitPersonForm)}
        />
      </FormProvider>
    </div>
  )
}

export default CollaboratorForm
